/**
 * 文件扩展名和 MIME 类型处理工具
 * 提供扩展名验证、MIME 类型转换等功能
 */

/**
 * MIME 类型到扩展名的映射表
 */
const MIME_TO_EXTENSION_MAP = {
  // 压缩文件
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'application/x-rar-compressed': 'rar',
  'application/x-rar': 'rar',
  'application/x-7z-compressed': '7z',
  'application/gzip': 'gz',
  'application/x-gzip': 'gz',
  'application/x-tar': 'tar',
  'application/x-bzip2': 'bz2',
  
  // 文档
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/rtf': 'rtf',
  
  // 图片
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/x-ms-bmp': 'bmp',
  'image/tiff': 'tiff',
  'image/x-icon': 'ico',
  
  // 音频
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/wav': 'wav',
  'audio/wave': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/aac': 'aac',
  'audio/x-aac': 'aac',
  'audio/flac': 'flac',
  'audio/x-flac': 'flac',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  
  // 视频
  'video/mp4': 'mp4',
  'video/mpeg': 'mpeg',
  'video/quicktime': 'mov',
  'video/x-msvideo': 'avi',
  'video/webm': 'webm',
  'video/x-flv': 'flv',
  'video/x-matroska': 'mkv',
  
  // 其他
  'application/json': 'json',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'text/html': 'html',
  'text/css': 'css',
  'text/javascript': 'js',
  'application/javascript': 'js',
  'application/x-javascript': 'js',
  'application/typescript': 'ts',
  
  // 安装包
  'application/vnd.android.package-archive': 'apk',
  'application/x-msdownload': 'exe',
  'application/x-apple-diskimage': 'dmg'
}

/**
 * 常见的有效文件扩展名列表
 */
const VALID_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico',
  'mp4', 'mpeg', 'mov', 'avi', 'webm', 'flv', 'mkv',
  'mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf',
  'zip', 'rar', '7z', 'gz', 'tar', 'bz2',
  'json', 'xml', 'html', 'css', 'js', 'ts', 'jsx', 'tsx', 'vue',
  'apk', 'exe', 'dmg', 'pkg', 'deb', 'rpm',
  'iso', 'img', 'bin', 'dat'
]

/**
 * 从 MIME 类型获取文件扩展名
 * @param {string} mimetype - MIME 类型字符串
 * @returns {string} 文件扩展名（不含点号），如果无法识别则返回空字符串
 */
export function getExtensionFromMimetype(mimetype) {
  if (!mimetype || typeof mimetype !== 'string') return ''
  
  const mime = mimetype.toLowerCase().trim()
  
  // 先尝试精确匹配
  if (MIME_TO_EXTENSION_MAP[mime]) {
    return MIME_TO_EXTENSION_MAP[mime]
  }
  
  // 模糊匹配（兼容性处理）
  if (mime.includes('zip')) return 'zip'
  if (mime.includes('rar')) return 'rar'
  if (mime.includes('7z')) return '7z'
  if (mime.includes('gzip') || mime.includes('gz')) return 'gz'
  if (mime.includes('tar')) return 'tar'
  if (mime.includes('pdf')) return 'pdf'
  if (mime.includes('msword') || mime.includes('wordprocessing')) {
    return mime.includes('openxmlformats') ? 'docx' : 'doc'
  }
  if (mime.includes('excel') || mime.includes('spreadsheet')) {
    return mime.includes('openxmlformats') ? 'xlsx' : 'xls'
  }
  if (mime.includes('powerpoint') || mime.includes('presentation')) {
    return mime.includes('openxmlformats') ? 'pptx' : 'ppt'
  }
  if (mime.includes('png')) return 'png'
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg'
  if (mime.includes('gif')) return 'gif'
  if (mime.includes('webp')) return 'webp'
  if (mime.includes('svg')) return 'svg'
  if (mime.includes('bmp')) return 'bmp'
  if (mime.includes('mp3') || (mime.includes('mpeg') && mime.startsWith('audio'))) return 'mp3'
  if (mime.includes('wav')) return 'wav'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('aac')) return 'aac'
  if (mime.includes('flac')) return 'flac'
  if (mime.includes('mp4')) return mime.startsWith('video') ? 'mp4' : 'm4a'
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('quicktime')) return 'mov'
  if (mime.includes('msvideo') || mime.includes('avi')) return 'avi'
  
  // 尝试从 mimetype 中提取扩展名 (例如: image/png -> png)
  const parts = mime.split('/')
  if (parts.length === 2) {
    const subtype = parts[1].split(';')[0].trim()
    // 移除 x- 前缀和 vnd. 前缀
    const cleaned = subtype.replace(/^(x-|vnd\.)/, '')
    // 只返回简单的扩展名（不包含特殊字符，长度合理）
    if (/^[a-z0-9]+$/.test(cleaned) && cleaned.length >= 2 && cleaned.length <= 5) {
      return cleaned
    }
  }
  
  return ''
}

/**
 * 从文件名或 URL 中提取扩展名
 * @param {string} name - 文件名
 * @param {string} url - 文件 URL（可选）
 * @returns {string} 文件扩展名（不含点号），如果无法提取则返回空字符串
 */
export function getExtensionFromNameOrUrl(name, url) {
  const n = String(name || '').trim()
  if (n && n.includes('.')) {
    const ext = n.split('.').pop()
    return String(ext || '').toLowerCase()
  }

  const u = String(url || '').split('?')[0].split('#')[0].trim()
  if (!u) return ''
  const last = u.lastIndexOf('.')
  if (last === -1) return ''
  const ext = u.slice(last + 1).toLowerCase()
  // 过滤掉包含斜杠的无效扩展名
  if (ext.includes('/')) return ''
  return ext
}

/**
 * 从 URL 中提取扩展名
 * @param {string} url - 文件 URL
 * @returns {string} 文件扩展名（不含点号），如果无法提取则返回空字符串
 */
export function getExtensionFromUrl(url) {
  const s = String(url || '').split('?')[0].split('#')[0].toLowerCase()
  const idx = s.lastIndexOf('.')
  if (idx === -1) return ''
  const ext = s.slice(idx + 1)
  if (!ext) return ''
  if (ext.includes('/')) return ''
  return ext
}

/**
 * 验证扩展名是否有效
 * @param {string} ext - 文件扩展名（不含点号）
 * @returns {boolean} 是否为有效的扩展名
 */
export function isValidExtension(ext) {
  if (!ext || typeof ext !== 'string') return false
  const e = ext.toLowerCase().trim()
  return VALID_EXTENSIONS.includes(e)
}

/**
 * 智能获取文件扩展名
 * 优先级：有效的 URL 扩展名 > MIME 类型推断 > 无效的 URL 扩展名
 * @param {Object} options - 配置选项
 * @param {string} options.url - 文件 URL
 * @param {string} options.mimetype - MIME 类型
 * @param {string} options.filename - 文件名（可选）
 * @returns {string} 文件扩展名（不含点号）
 */
export function getSmartExtension({ url, mimetype, filename }) {
  // 1. 尝试从文件名获取扩展名
  if (filename) {
    const nameExt = getExtensionFromNameOrUrl(filename)
    if (nameExt && isValidExtension(nameExt)) {
      return nameExt
    }
  }
  
  // 2. 尝试从 URL 获取扩展名
  const urlExt = getExtensionFromUrl(url)
  const mimeExt = getExtensionFromMimetype(mimetype)
  
  if (urlExt && isValidExtension(urlExt)) {
    // URL 扩展名有效，使用它
    return urlExt
  } else if (mimeExt) {
    // URL 扩展名无效或不存在，使用 mimetype 推断的扩展名
    return mimeExt
  } else if (urlExt) {
    // 没有 mimetype，但有 URL 扩展名（即使可能无效）
    return urlExt
  }
  
  return ''
}

/**
 * 确保文件名有正确的扩展名
 * @param {string} filename - 原始文件名
 * @param {string} mimetype - MIME 类型
 * @param {string} url - 文件 URL（可选）
 * @returns {string} 带有正确扩展名的文件名
 */
export function ensureFileExtension(filename, mimetype, url) {
  if (!filename) return ''
  
  const name = String(filename).trim()
  const hasExt = name.includes('.') && name.split('.').pop()
  const currentExt = hasExt ? name.split('.').pop().toLowerCase() : ''
  
  // 如果当前扩展名有效，直接返回
  if (currentExt && isValidExtension(currentExt)) {
    return name
  }
  
  // 获取正确的扩展名
  const correctExt = getSmartExtension({ url, mimetype, filename: name })
  
  if (!correctExt) {
    return name
  }
  
  // 如果有无效的扩展名，替换它；否则追加扩展名
  if (hasExt) {
    return `${name.slice(0, name.lastIndexOf('.'))}.${correctExt}`
  } else {
    return `${name}.${correctExt}`
  }
}

/**
 * 获取扩展名对应的 MIME 类型（反向查找）
 * @param {string} extension - 文件扩展名（不含点号）
 * @returns {string} MIME 类型，如果找不到则返回空字符串
 */
export function getMimetypeFromExtension(extension) {
  if (!extension || typeof extension !== 'string') return ''
  
  const ext = extension.toLowerCase().trim()
  
  // 反向查找映射表
  for (const [mime, mappedExt] of Object.entries(MIME_TO_EXTENSION_MAP)) {
    if (mappedExt === ext) {
      return mime
    }
  }
  
  // 通用的 MIME 类型推断
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`
  }
  if (['mp4', 'mpeg', 'mov', 'avi', 'webm', 'flv', 'mkv'].includes(ext)) {
    return `video/${ext}`
  }
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) {
    return `audio/${ext === 'mp3' ? 'mpeg' : ext}`
  }
  
  return ''
}
